import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  styled,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import useAdd from '../../api/useAdd';
import useFetchObjects from '../../api/useFetchObjects';
import { useAuth } from '../../context/AuthContext';

// RTL-aware styled components
const RTLTextField = styled(TextField)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      padding: '10px 14px',
    },
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

const RTLFormControl = styled(FormControl)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
    },
    '& .MuiSelect-select': {
      paddingRight: '32px',
      textAlign: 'right',
      paddingLeft: '32px',
    },
    '& .MuiSelect-icon': {
      right: 'auto',
      left: 7,
      pointerEvents: 'none',
      color: theme.palette.action.active,
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

const RTLBox = styled(Box)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

const DirectorateCreate = () => {
  const { t, i18n } = useTranslation(['directorates', 'common', 'validation']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';

  const { user } = useAuth();
  const token = user?.token;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    deputy_ministry_id: '',
  });

  // Validation state
  const [errors, setErrors] = useState({});

  // Fetch deputy ministries
  const { 
    data: deputyMinistriesData, 
    isLoading: loadingDeputyMinistries 
  } = useFetchObjects('deputy-ministries', {}, token);
  
  const deputyMinistries = Array.isArray(deputyMinistriesData) ? deputyMinistriesData : (deputyMinistriesData?.data || []);

  const { handleAdd, loading: isAdding } = useAdd('directorates', token);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation:required');
    }
    
    if (!formData.deputy_ministry_id) {
      newErrors.deputy_ministry_id = t('validation:required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      await handleAdd(formData);
      toast.success(t('directorates:create_success'));
      navigate('/directorates');
    } catch (error) {
      console.error('Error creating directorate:', error);
      toast.error(error.response?.data?.message || t('common:error_creating'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <RTLBox sx={{ mb: 4 }} isRtl={isRtl} dir={isRtl ? 'rtl' : 'ltr'}>
        <Button
          component={RouterLink}
          to="/directorates"
          startIcon={!isRtl && <ArrowBackIcon />}
          endIcon={isRtl && <ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          {t('common:back')}
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('directorates:create_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('directorates:create_description')}
        </Typography>
      </RTLBox>

      <Box
        component="form"
        onSubmit={handleSubmit}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ textAlign: isRtl ? 'right' : 'left' }}>
              {t('directorates:basic_info')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <RTLTextField
                  fullWidth
                  name="name"
                  label={t('directorates:name')}
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  isRtl={isRtl}
                  InputProps={{
                    dir: isRtl ? 'rtl' : 'ltr',
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <RTLFormControl fullWidth error={!!errors.deputy_ministry_id} required isRtl={isRtl}>
                  <InputLabel id="deputy-ministry-label">
                    {t('directorates:deputy_ministry')}
                  </InputLabel>
                  <Select
                    labelId="deputy-ministry-label"
                    name="deputy_ministry_id"
                    label={t('directorates:deputy_ministry')}
                    value={formData.deputy_ministry_id}
                    onChange={handleChange}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: isRtl ? 'right' : 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: isRtl ? 'right' : 'left',
                      },
                      PaperProps: {
                        style: {
                          direction: isRtl ? 'rtl' : 'ltr',
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{t('directorates:select')}</em>
                    </MenuItem>
                    {loadingDeputyMinistries ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} /> {t('common:loading')}
                      </MenuItem>
                    ) : (
                      deputyMinistries.map((ministry) => (
                        <MenuItem key={ministry.id} value={ministry.id}>
                          {ministry.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.deputy_ministry_id && (
                    <FormHelperText>{errors.deputy_ministry_id}</FormHelperText>
                  )}
                </RTLFormControl>
              </Grid>
              
              <Grid item xs={12}>
                <RTLTextField
                  fullWidth
                  name="description"
                  label={t('directorates:description')}
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  isRtl={isRtl}
                  InputProps={{
                    dir: isRtl ? 'rtl' : 'ltr',
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 2 }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => navigate('/directorates')}
            disabled={loading}
          >
            {t('common:cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || isAdding}
          >
            {(loading || isAdding) ? (
              <CircularProgress size={24} />
            ) : (
              t('common:save')
            )}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default DirectorateCreate; 